import { useQuery } from "@tanstack/react-query";
import {
  getAbandonedCarts,
  getBankOrders,
  getBestClients,
  getCardOrders,
  getClients,
  getDriversVerifications,
  getGiftOrders,
  getMostDonatedProjects,
  getNewOrders,
  getOrders,
  getOrdersAmount,
  getProjectsVerifications,
  getRecurrentOrders,
  getVerifications,
} from "./api.js";
import { reactQueryCacheAndRefetchOptions } from "@";

// Query keys for React Query
export const dashboardKeys = {
  all: ["dashboard"],
  orders: (time) => [...dashboardKeys.all, "orders", time],
  giftOrders: (time) => [...dashboardKeys.all, "giftOrders", time],
  abandonedCarts: (time) => [...dashboardKeys.all, "abandonedCarts", time],
  recurrentOrders: (time) => [...dashboardKeys.all, "recurrentOrders", time],
  clients: (time) => [...dashboardKeys.all, "clients", time],
  newOrders: (time) => [...dashboardKeys.all, "newOrders", time],
  verifications: (time) => [...dashboardKeys.all, "verifications", time],
  mostDonatedProjects: (time) => [
    ...dashboardKeys.all,
    "mostDonatedProjects",
    time,
  ],
  cardOrders: (time) => [...dashboardKeys.all, "cardOrders", time],
  bankOrders: (time) => [...dashboardKeys.all, "bankOrders", time],
  ordersAmount: (time) => [...dashboardKeys.all, "ordersAmount", time],
  bestClients: (time) => [...dashboardKeys.all, "bestClients", time],
  driversVerifications: (time) => [
    ...dashboardKeys.all,
    "driversVerifications",
    time,
  ],
  projectsVerifications: (time) => [
    ...dashboardKeys.all,
    "projectsVerifications",
    time,
  ],
};

// Individual query hooks
export const useOrdersQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.orders(time),
    queryFn: async () => await getOrders(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useGiftOrdersQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.giftOrders(time),
    queryFn: async () => await getGiftOrders(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useAbandonedCartsQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.abandonedCarts(time),
    queryFn: async () => await getAbandonedCarts(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useRecurrentOrdersQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.recurrentOrders(time),
    queryFn: async () => await getRecurrentOrders(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useClientsQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.clients(time),
    queryFn: async () => await getClients(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useNewOrdersQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.newOrders(time),
    queryFn: async () => await getNewOrders(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useVerificationsQuery = (time = 3, options = {}) => {
  return useQuery({
    queryKey: dashboardKeys.verifications(time),
    queryFn: async () => await getVerifications(time),
    ...reactQueryCacheAndRefetchOptions,
    ...options,
  });
};

export const useMostDonatedProjectsQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.mostDonatedProjects(time),
    queryFn: async () => await getMostDonatedProjects(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useCardOrdersQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.cardOrders(time),
    queryFn: async () => await getCardOrders(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useBankOrdersQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.bankOrders(time),
    queryFn: async () => await getBankOrders(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useOrdersAmountQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.ordersAmount(time),
    queryFn: async () => await getOrdersAmount(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useBestClientsQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.bestClients(time),
    queryFn: async () => await getBestClients(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useDriversVerificationsQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.driversVerifications(time),
    queryFn: async () => await getDriversVerifications(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};

export const useProjectsVerificationsQuery = (time = 3) => {
  return useQuery({
    queryKey: dashboardKeys.projectsVerifications(time),
    queryFn: async () => await getProjectsVerifications(time),
    ...reactQueryCacheAndRefetchOptions,
  });
};
